import {
  CheckCircleFilled,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  LeftOutlined,
  MessageOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clearCache } from 'ahooks';
import { Alert, Input, InputNumber, Progress, Radio, Select, Spin } from 'antd';
import { AxiosError } from 'axios';
import PageTopBar from 'components/navigation/PageTopBar';
import useIsKeyboardVisible from 'hooks/useIsKeyboardVisible';
import posthog from 'posthog-js';
import { usePushNotificationOptIn } from 'push/PushNotificationOptInContext';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ApiQuestionnaireManager from 'services/api/ApiQuestionnaireManager';
import {
  QuestionnaireAnswers,
  QuestionnaireQuestion,
  QuestionnaireResponseDTO,
  QuestionnaireSection,
} from 'services/api/models/QuestionnaireDTO';
import { routes } from 'services/RouteService';
import { contentActions } from 'store/content';
import { selectCurrentUser } from 'store/users/selectors';
import { Button } from 'components/ui';
import { colors, palette, radius } from 'design';

/** Safely renders a string that may contain only <b> and <br> tags. */
function RichText({ text }: { text: string }) {
  const parts = text.split(/(<b>.*?<\/b>|<br\s*\/?>)/s);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('<b>')) {
          return <b key={i}>{part.slice(3, -4)}</b>;
        }
        if (part.startsWith('<br')) {
          return <br key={i} />;
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}

const useStyles = createUseStyles({
  '@keyframes checkmarkPop': {
    '0%': { transform: 'scale(0) rotate(-20deg)', opacity: 0 },
    '65%': { transform: 'scale(1.3) rotate(8deg)', opacity: 1 },
    '100%': { transform: 'scale(1) rotate(0deg)', opacity: 1 },
  },
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.surface.page,
    overflow: 'hidden',
    alignItems: 'center',
  },
  scrollContainer: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 600,
    width: '100%',
  },
  progressContainer: {
    padding: '12px 20px 12px',
    maxWidth: 600,
    width: '100%',
  },
  sectionLabel: {
    color: colors.text.tertiary,
    // fontSize: 16,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    margin: '24px 0 24px',
  },
  contentContainer: {
    flex: 1,
    padding: '20px 20px 80px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  contentContainerLastSection: {
    flex: 1,
    padding: '20px 20px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  questionBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginBottom: 12,
  },
  questionSeparator: {
    border: 'none',
    borderTop: `1px solid ${colors.border.subtle}`,
    margin: '24px 0',
  },
  questionDisplay: {
    color: colors.text.primary,
    margin: 0,
    fontWeight: 500,
    fontSize: '1rem',
  },
  questionLabel: {
    color: colors.text.primary,
    margin: 0,
    fontWeight: 500,
    fontSize: '1rem',
  },
  questionDescription: {
    color: colors.text.tertiary,
    margin: 0,
    marginTop: 4,
    fontSize: '0.85rem',
  },
  choiceButton: {
    width: '100%',
    height: 'auto',
    minHeight: 44,
    whiteSpace: 'normal',
    textAlign: 'center',
    marginBottom: 8,
    lineHeight: 1.4,
    padding: '8px 14px',
  },
  choiceButtonSelected: {
    backgroundColor: palette.mint[50],
    color: colors.brand.accentStrong,
    fontWeight: 600,
    borderColor: colors.brand.accent,
  },
  choiceButtonUnselected: {
    backgroundColor: 'white',
    color: colors.text.tertiary,
  },
  textArea: {
    color: colors.text.primary,
    borderRadius: 8,
    resize: 'none',
  },
  navigationBar: {
    width: '100%',
    maxWidth: 600,
    position: 'sticky',
    bottom: 0,
    padding: '12px 20px',
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    backgroundColor: colors.surface.page,
  },
  navigationBarInline: {
    width: '100%',
    maxWidth: 600,
    paddingTop: '12px',
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  navButton: {
    flex: 1,
  },
  lastSectionCtaWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 32,
    paddingBottom: 40,
  },
  savingIndicator: {
    color: colors.text.disabled,
    fontSize: 16,
    transition: 'opacity 0.4s ease',
  },
  savingIndicatorVisible: {
    opacity: 1,
  },
  savingIndicatorHidden: {
    opacity: 0,
  },
  completedContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 24px',
    gap: 20,
    textAlign: 'center',
  },
  completedTitle: {
    color: colors.text.primary,
    fontWeight: 700,
    margin: 0,
    '& .anticon': {
      marginTop: 24,
      fontSize: 48,
      display: 'inline-block',
      animation: '$checkmarkPop 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
    },
  },
  completedDescription: {
    color: colors.text.tertiary,
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 16,
    '& .ant-spin-dot': {
      color: colors.brand.accentStrong,
    },
  },
  greetingContainer: {
    padding: '40px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  greetingTitle: {
    color: colors.text.primary,
    margin: 0,
  },
  greetingDescriptionList: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 16,
    gap: 24,
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  greetingDescriptionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    '& .anticon': {
      marginTop: 3,
    },
  },
  greetingDescriptionIcon: {
    fontSize: 18,
    color: colors.brand.accentStrong,
    flexShrink: 0,
    marginTop: 1,
  },
  greetingDescriptionText: {
    color: colors.text.primary,
    opacity: 0.9,
    margin: 0,
    fontSize: '0.95rem',
    lineHeight: 1.5,
  },
  yearSelect: {
    width: '100%',
    height: 44,
  },
  sectionNote: {
    borderRadius: 10,
    marginBottom: 16,
    marginTop: 32,
  },
  tagSelect: {
    width: '100%',
    '&.ant-select, & .ant-select-selector': {
      backgroundColor: colors.brand.accentSubtle,
      border: 'none',
      borderRadius: radius.xl,
      minHeight: 44,
      padding: '4px 8px',
    },
    '& .ant-select-selection-item': {
      backgroundColor: colors.brand.accentSubtle,
      color: colors.text.secondary,
      border: 'none',
      borderRadius: radius.lg,
      fontWeight: '600',
      height: 'auto',
      lineHeight: '1.3',
      padding: '3px 6px',
      whiteSpace: 'normal',
      '& svg': {
        color: colors.text.secondary,
      },
    },
    '& .ant-select-selection-item-content': {
      overflow: 'visible',
      whiteSpace: 'normal',
    },
    '& .ant-select-selection-item-remove': {
      color: 'white',
      '& .anticon': {
        color: 'white',
      },
    },
    '& .ant-select-arrow': {
      color: colors.brand.accentStrong,
    },
    '& .ant-select-selection-placeholder': {
      color: `${colors.text.primary}66`,
    },
  },
  numberInput: {
    width: '100%',
    height: 44,
    borderRadius: 8,
    '& .ant-input-number-input': {
      color: colors.text.primary,
    },
    '& .ant-input-number-handler-wrap': {
      borderRadius: '0 8px 8px 0',
      '& .ant-input-number-handler': {
        borderColor: `${colors.brand.accent}33`,
        color: colors.brand.accentStrong,
      },
    },
  },
  selectDropdownItems: {
    '& .ant-select-item': {
      borderRadius: radius.lg,
    },
    '& .ant-select-item-option': {
      minHeight: 44,
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
    '& .ant-select-item-option-content': {
      whiteSpace: 'normal',
      overflow: 'visible',
    },
    '& .ant-select-item-option-selected': {
      backgroundColor: colors.brand.accentSubtle,
      color: colors.text.secondary,
    },
  },
  radioGroupHorizontal: {
    display: 'flex',
    width: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    '& .ant-radio-button-wrapper': {
      flex: '1 1 auto',
      textAlign: 'center',
      backgroundColor: 'white',
      color: colors.text.tertiary,
      whiteSpace: 'normal',
      height: 'auto',
      lineHeight: 1.3,
      padding: '10px 6px',
    },
    '& .ant-radio-button-wrapper-checked': {
      backgroundColor: 'white',
      borderColor: colors.brand.accent,
      color: colors.brand.accentStrong,
      fontWeight: 600,
    },
  },
});

function SelectWithScroll(props: React.ComponentProps<typeof Select>) {
  const wrapRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      <Select
        style={{ width: '100%' }}
        {...props}
        onOpenChange={open => {
          if (open)
            setTimeout(
              () => wrapRef.current?.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
              50,
            );
          props.onOpenChange?.(open);
        }}
      />
    </div>
  );
}

function isQuestionVisible(question: QuestionnaireQuestion, answers: QuestionnaireAnswers): boolean {
  if (!question.condition) return true;
  const { question_id, value, contains, not_equals } = question.condition as any;
  const answer = answers[question_id];
  if (value !== undefined) return answer === value;
  if (contains !== undefined) return Array.isArray(answer) && answer.includes(contains);
  if (not_equals !== undefined) return answer !== not_equals && answer !== undefined && answer !== null;
  return true;
}

interface SectionFormProps {
  section: QuestionnaireSection;
  answers: QuestionnaireAnswers;
  onChange: (questionId: string, value: QuestionnaireAnswers[string]) => void;
}

function SectionForm({ section, answers, onChange }: SectionFormProps) {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {section.note && (
        <Alert className={styles.sectionNote} type="warning" showIcon description={<RichText text={section.note} />} />
      )}
      {section.questions
        .filter(q => isQuestionVisible(q, answers))
        .map(question => {
          if (question.type === 'separator') {
            return <hr key={question.id} className={styles.questionSeparator} />;
          }
          if (question.type === 'info') {
            return (
              <div key={question.id} data-info-block>
                <Alert
                  className={styles.sectionNote}
                  type="info"
                  showIcon
                  description={<RichText text={question.label} />}
                />
              </div>
            );
          }
          if (question.type === 'display') {
            return (
              <p key={question.id} className={styles.questionDisplay}>
                {question.label}
              </p>
            );
          }
          return (
            <div key={question.id} className={styles.questionBlock} data-question-block>
              {question.note && (
                <Alert
                  className={styles.sectionNote}
                  type="warning"
                  showIcon
                  description={<RichText text={question.note} />}
                />
              )}
              <div>
                <p className={styles.questionLabel}>{question.label}</p>
                {question.description && <p className={styles.questionDescription}>{question.description}</p>}
              </div>
              {question.type === 'select_year' && (
                <SelectWithScroll
                  className={styles.yearSelect}
                  classNames={{ popup: { root: styles.selectDropdownItems } }}
                  showSearch={{
                    optionFilterProp: 'label',
                    filterSort: (a: any, b: any) =>
                      (b?.label ?? '').toLowerCase().localeCompare((a?.label ?? '').toLowerCase()),
                  }}
                  placeholder={t('medicalProfile.questionnaire.yearPlaceholder')}
                  value={answers[question.id] ? String(answers[question.id]) : undefined}
                  onChange={val => onChange(question.id, Number(val))}
                  options={Array.from({ length: new Date().getFullYear() - 1900 }, (_, index) => ({
                    label: `${1900 + index}`,
                    value: `${1900 + index}`,
                  }))}
                />
              )}
              {question.type === 'boolean' && (
                <Radio.Group
                  className={styles.radioGroupHorizontal}
                  size="large"
                  value={answers[question.id]}
                  onChange={e => onChange(question.id, e.target.value)}
                  options={[
                    { label: t('medicalProfile.questionnaire.yes'), value: true },
                    { label: t('medicalProfile.questionnaire.no'), value: false },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              )}
              {question.type === 'single_select' && question.choices && question.layout === 'horizontal' && (
                <Radio.Group
                  className={styles.radioGroupHorizontal}
                  size="large"
                  value={answers[question.id]}
                  onChange={e => onChange(question.id, e.target.value)}
                  options={question.choices.map(c => ({ label: c.label, value: c.value }))}
                  optionType="button"
                  buttonStyle="solid"
                />
              )}
              {question.type === 'single_select' && question.choices && question.layout !== 'horizontal' && (
                <div>
                  {question.choices.map(choice => (
                    <Button
                      key={choice.value}
                      className={`${styles.choiceButton} ${answers[question.id] === choice.value ? styles.choiceButtonSelected : styles.choiceButtonUnselected}`}
                      onClick={() => onChange(question.id, choice.value)}
                    >
                      {choice.label}
                    </Button>
                  ))}
                </div>
              )}
              {question.type === 'multi_select' && question.choices && question.layout === 'tag_select' && (
                <SelectWithScroll
                  className={styles.tagSelect}
                  classNames={{ popup: { root: styles.selectDropdownItems } }}
                  mode="multiple"
                  size="large"
                  allowClear
                  showSearch={false}
                  placeholder={t('medicalProfile.questionnaire.selectPlaceholder' as any)}
                  value={answers[question.id] || []}
                  onChange={val => onChange(question.id, val as string[])}
                  options={question.choices.map(c => ({ label: c.label, value: c.value }))}
                />
              )}
              {question.type === 'multi_select' && question.choices && question.layout !== 'tag_select' && (
                <div>
                  {question.choices.map(choice => {
                    const selected =
                      Array.isArray(answers[question.id]) && (answers[question.id] as string[]).includes(choice.value);
                    return (
                      <Button
                        key={choice.value}
                        className={`${styles.choiceButton} ${selected ? styles.choiceButtonSelected : styles.choiceButtonUnselected}`}
                        onClick={() => {
                          const current = (answers[question.id] as string[]) || [];
                          if (selected) {
                            onChange(
                              question.id,
                              current.filter(v => v !== choice.value),
                            );
                          } else {
                            onChange(question.id, [...current, choice.value]);
                          }
                        }}
                      >
                        {choice.label}
                      </Button>
                    );
                  })}
                </div>
              )}
              {question.type === 'text' && (
                <Input.TextArea
                  className={styles.textArea}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  placeholder={t('medicalProfile.questionnaire.textareaPlaceholder' as any)}
                  value={(answers[question.id] as string) || ''}
                  onChange={e => onChange(question.id, e.target.value)}
                />
              )}
              {question.type === 'number' && (
                <InputNumber
                  className={styles.numberInput}
                  size="large"
                  min={question.min}
                  max={question.max}
                  step={question.step ?? 1}
                  value={(answers[question.id] as number) ?? null}
                  onChange={val => onChange(question.id, val)}
                />
              )}
              {question.conditional_note &&
                Array.isArray(answers[question.id]) &&
                (answers[question.id] as string[]).includes(question.conditional_note.when_contains) && (
                  <Alert
                    className={styles.sectionNote}
                    type={question.conditional_note.type ?? 'warning'}
                    showIcon
                    description={<RichText text={question.conditional_note.note} />}
                  />
                )}
            </div>
          );
        })}
    </div>
  );
}

export default function MedicalProfileQuestionnaire() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const styles = useStyles();
  const currentUser = useSelector(selectCurrentUser);

  const queryClient = useQueryClient();
  const { requestOptIn } = usePushNotificationOptIn();

  const [response, setResponse] = useState<QuestionnaireResponseDTO | null>(null);
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({});
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [showGreeting, setShowGreeting] = useState(false);
  // True when the questionnaire is complete but pending_review or in_review — shows the info/processing page.
  const [showProcessingInfo, setShowProcessingInfo] = useState(false);
  // True only when the user just submitted the questionnaire in this session.
  // When re-opening an already-complete questionnaire the form is shown instead.
  const [justCompleted, setJustCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Tracks whether the response was already complete when the page loaded (edit mode).
  const [isEditingCompleted, setIsEditingCompleted] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentSectionIndex]);

  const { isKeyboardVisible } = useIsKeyboardVisible();

  // When the keyboard finishes appearing the viewport is at its final size —
  // scroll the focused element into view now that the layout has settled.
  useEffect(() => {
    if (isKeyboardVisible) {
      setTimeout(() => {
        const active = document.activeElement as HTMLElement | null;
        (active?.closest('[data-question-block]') ?? active)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [isKeyboardVisible]);

  const { data: schemaData, isLoading: loadingSchema } = useQuery({
    queryKey: ['questionnaire', 'schema'],
    queryFn: () => ApiQuestionnaireManager.getSchema(),
    staleTime: 1000 * 60 * 60, // schema rarely changes
  });

  const { data: serverResponse, isLoading: loadingResponse } = useQuery<QuestionnaireResponseDTO | null>({
    queryKey: ['questionnaire', 'myResponse', currentUser?.id],
    queryFn: () =>
      ApiQuestionnaireManager.getMyResponse().catch(err => {
        if (err instanceof AxiosError && err.response?.status === 404) return null;
        throw err;
      }),
    staleTime: 1000 * 60 * 5,
  });

  const schema = schemaData?.schema ?? null;
  const totalSections = schema?.sections.length ?? 0;
  const loadingInitial = loadingSchema || loadingResponse;

  // Initialise local state once from server data. A ref guards against
  // re-initialising on background refetches while the user is filling in answers.
  useEffect(() => {
    if (hasInitialized.current || !schemaData || serverResponse === undefined) return;
    hasInitialized.current = true;

    if (serverResponse) {
      setResponse(serverResponse);
      setAnswers(serverResponse.answers);
      if (serverResponse.is_complete) {
        setIsEditingCompleted(true);
        setCurrentSectionIndex(0);
        // For pending_review or in_review states, show the processing info page first.
        if (!serverResponse.reviewed_at) {
          setShowProcessingInfo(true);
        } else {
          setShowGreeting(false);
        }
      } else {
        setCurrentSectionIndex(serverResponse.current_section_index);
        if (serverResponse.current_section_index > 0 || Object.keys(serverResponse.answers).length > 0) {
          setShowGreeting(false);
        } else {
          setShowGreeting(true);
        }
      }
    } else {
      setShowGreeting(true);
    }
  }, [schemaData, serverResponse]);

  const { mutate: createResponse } = useMutation({
    mutationFn: () => ApiQuestionnaireManager.createOrResumeResponse(),
    onSuccess: data => {
      setResponse(data);
      setShowGreeting(false);
      queryClient.setQueryData(['questionnaire', 'myResponse', currentUser?.id], data);
    },
  });

  // Every saved answer feeds the user's interest profile on the backend: drop
  // the suggested articles kept for this session so the next visit to the
  // homepage or the articles page fetches a freshly personalised list.
  const invalidateSuggestedPosts = () => {
    dispatch(contentActions.clearSuggestedPosts());
    clearCache('content.suggestedPosts');
    clearCache('content.searchResults::');
  };

  const updateResponseCache = (patch: Partial<QuestionnaireResponseDTO>) => {
    queryClient.setQueryData(
      ['questionnaire', 'myResponse', currentUser?.id],
      (old: QuestionnaireResponseDTO | null | undefined) => (old ? { ...old, ...patch } : old),
    );
  };

  const saveProgress = (newAnswers: QuestionnaireAnswers, sectionIndex: number) => {
    if (!response) return;
    setIsSaving(true);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      // Optimistically push the latest answers into the shared cache so the
      // homepage card reflects the correct state immediately on navigation.
      updateResponseCache({ answers: newAnswers, current_section_index: sectionIndex });
      ApiQuestionnaireManager.saveProgress(response.id, {
        answers: newAnswers,
        current_section_index: sectionIndex,
      })
        .then(() => {
          invalidateSuggestedPosts();
          setIsSaving(false);
        })
        .catch(() => setIsSaving(false));
    }, 800);
  };

  const handleAnswerChange = (questionId: string, value: QuestionnaireAnswers[string]) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    saveProgress(newAnswers, currentSectionIndex);
  };

  const { isPending: completing, mutate: runComplete } = useMutation({
    mutationFn: () =>
      ApiQuestionnaireManager.complete(response!.id, {
        answers,
        current_section_index: currentSectionIndex,
      }),
    onSuccess: () => {
      posthog.capture('medical_profile.questionnaire_completed');
      invalidateSuggestedPosts();
      queryClient.setQueryData(
        ['questionnaire', 'myResponse', currentUser?.id],
        (old: QuestionnaireResponseDTO | null | undefined) =>
          old ? { ...old, is_complete: true, answers, current_section_index: currentSectionIndex } : old,
      );
      setJustCompleted(true);
      requestOptIn('questionnaire_completed');
    },
  });

  const flushSave = async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (!response) return;
    setIsSaving(true);
    try {
      await ApiQuestionnaireManager.saveProgress(response.id, {
        answers,
        current_section_index: currentSectionIndex,
      });
      updateResponseCache({ answers, current_section_index: currentSectionIndex });
      invalidateSuggestedPosts();
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    if (!schema) return;
    await flushSave();

    const nextIndex = currentSectionIndex + 1;
    const isLast = nextIndex >= totalSections;

    if (isLast) {
      if (isEditingCompleted) {
        // Already complete: progress was flushed above, just show the completed screen
        setJustCompleted(true);
      } else {
        runComplete();
      }
    } else {
      setCurrentSectionIndex(nextIndex);
      if (response) {
        updateResponseCache({ current_section_index: nextIndex });
        ApiQuestionnaireManager.saveProgress(response.id, {
          answers,
          current_section_index: nextIndex,
        });
      }
    }
  };

  const handlePrev = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
    } else {
      setShowGreeting(true);
    }
  };

  if (loadingInitial) {
    return (
      <div className={styles.pageContainer}>
        <PageTopBar
          title={t('medicalProfile.questionnaire.title')}
          backButton
          onBackButtonClicked={() => navigate(routes.home.route)}
        />
        <div className={styles.loadingContainer}>
          <Spin size="large" />
          <p style={{ color: 'white' }}>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (justCompleted) {
    return (
      <div className={styles.pageContainer}>
        <PageTopBar title={t('medicalProfile.questionnaire.title')} />
        <div className={styles.completedContainer}>
          <h1 className={styles.completedTitle}>
            {t('medicalProfile.questionnaire.completed.title')}
            <br />
            <CheckCircleFilled className={styles.greetingDescriptionIcon} /> <br />
          </h1>
          <p className={styles.completedDescription}>
            <Trans i18nKey="medicalProfile.questionnaire.completed.description" components={{ b: <b /> }} />
          </p>
          <Button variant="primary" size="lg" style={{ marginTop: 16 }} onClick={() => navigate(routes.home.route)}>
            {t('medicalProfile.questionnaire.completed.goHome')}
          </Button>
        </div>
      </div>
    );
  }

  if (showProcessingInfo) {
    return (
      <div className={styles.pageContainer}>
        <PageTopBar
          title={t('medicalProfile.questionnaire.title')}
          backButton
          onBackButtonClicked={() => navigate(routes.home.route)}
        />
        <div className={styles.greetingContainer}>
          <h2 className={styles.greetingTitle}>{t('medicalProfile.questionnaire.processingInfo.title')}</h2>
          <ul className={styles.greetingDescriptionList}>
            <li className={styles.greetingDescriptionItem}>
              <MessageOutlined className={styles.greetingDescriptionIcon} />
              <p className={styles.greetingDescriptionText}>
                {<Trans i18nKey="medicalProfile.questionnaire.processingInfo.description1" components={{ b: <b /> }} />}
              </p>
            </li>
            <li className={styles.greetingDescriptionItem}>
              <ClockCircleOutlined className={styles.greetingDescriptionIcon} />
              <p className={styles.greetingDescriptionText}>
                {<Trans i18nKey="medicalProfile.questionnaire.processingInfo.description2" components={{ b: <b /> }} />}
              </p>
            </li>
            <li className={styles.greetingDescriptionItem}>
              <EditOutlined className={styles.greetingDescriptionIcon} />
              <p className={styles.greetingDescriptionText}>
                {<Trans i18nKey="medicalProfile.questionnaire.processingInfo.description3" components={{ b: <b /> }} />}
              </p>
            </li>
          </ul>
        </div>
        <div className={styles.navigationBar}>
          <Button variant="primary" block size="lg" onClick={() => setShowProcessingInfo(false)}>
            {t('medicalProfile.questionnaire.processingInfo.cta')}
          </Button>
        </div>
      </div>
    );
  }

  if (showGreeting) {
    return (
      <div className={styles.pageContainer}>
        <PageTopBar
          title={t('medicalProfile.questionnaire.title')}
          backButton
          onBackButtonClicked={() => navigate(routes.home.route)}
        />
        <div className={styles.greetingContainer}>
          <h2 className={styles.greetingTitle}>
            {t('medicalProfile.questionnaire.greeting')}
            <span style={{ color: colors.brand.accentStrong }}>{currentUser?.first_name}.</span>
          </h2>
          <ul className={styles.greetingDescriptionList}>
            <li className={styles.greetingDescriptionItem}>
              <ClockCircleOutlined className={styles.greetingDescriptionIcon} />
              <p className={styles.greetingDescriptionText}>
                {<Trans i18nKey="medicalProfile.questionnaire.description1" components={{ b: <b /> }} />}
              </p>
            </li>
            <li className={styles.greetingDescriptionItem}>
              <UserOutlined className={styles.greetingDescriptionIcon} />
              <p className={styles.greetingDescriptionText}>
                {<Trans i18nKey="medicalProfile.questionnaire.description2" components={{ b: <b /> }} />}
              </p>
            </li>
            <li className={styles.greetingDescriptionItem}>
              <CheckCircleOutlined className={styles.greetingDescriptionIcon} />
              <p className={styles.greetingDescriptionText}>
                {<Trans i18nKey="medicalProfile.questionnaire.description3" components={{ b: <b /> }} />}
              </p>
            </li>
          </ul>
        </div>
        <div className={styles.navigationBar}>
          <Button
            variant="primary"
            block
            size="lg"
            onClick={() => {
              posthog.capture('medical_profile.questionnaire_started', {
                is_resuming: Boolean(response),
              });
              if (response) {
                setShowGreeting(false);
              } else {
                createResponse();
              }
            }}
          >
            {response ? t('medicalProfile.questionnaire.continue') : t('medicalProfile.questionnaire.start')}
          </Button>
        </div>
      </div>
    );
  }

  if (!schema) return null;

  const currentSection: QuestionnaireSection = schema.sections[currentSectionIndex];
  const isLastSection = currentSectionIndex === totalSections - 1;
  const progressPercent = Math.round((100 * (currentSectionIndex + 1)) / totalSections);

  return (
    <div className={styles.pageContainer}>
      <PageTopBar title={t('medicalProfile.questionnaire.title')} />
      <div className={styles.progressContainer}>
        <Progress
          percent={progressPercent}
          strokeColor={colors.brand.accent}
          railColor={colors.brand.accentSubtle}
          showInfo={false}
        />
      </div>
      <div className={styles.scrollContainer} ref={scrollContainerRef}>
        <div className={isLastSection ? styles.contentContainerLastSection : styles.contentContainer}>
          <p className={styles.sectionLabel}>
            {currentSectionIndex + 1}. {currentSection.label}
          </p>
          <SectionForm section={currentSection} answers={answers} onChange={handleAnswerChange} />
          {isLastSection ? (
            <div className={styles.lastSectionCtaWrapper}>
              <div>
                <SyncOutlined
                  spin
                  style={{ marginRight: 8 }}
                  className={`${styles.savingIndicator} ${isSaving ? styles.savingIndicatorVisible : styles.savingIndicatorHidden}`}
                />
              </div>
              <Button variant="primary" block size="lg" loading={completing} onClick={handleNext}>
                {isEditingCompleted
                  ? t('medicalProfile.questionnaire.saveEdit')
                  : t('medicalProfile.questionnaire.finish')}
              </Button>
            </div>
          ) : (
            isKeyboardVisible && <NavBar />
          )}
        </div>
      </div>
      {!isKeyboardVisible && <NavBar />}
    </div>
  );

  function NavBar() {
    return (
      <div className={isKeyboardVisible ? styles.navigationBarInline : styles.navigationBar}>
        {isLastSection ? (
          // Empty div to push the button to the left
          <div></div>
        ) : (
          <div>
            <SyncOutlined
              spin
              style={{ marginRight: 16 }}
              className={`${styles.savingIndicator} ${isSaving ? styles.savingIndicatorVisible : styles.savingIndicatorHidden}`}
            />
            <Button variant="primary" className={styles.navButton} size="lg" loading={completing} onClick={handleNext}>
              {t('medicalProfile.questionnaire.save')}
            </Button>
          </div>
        )}
        <Button
          icon={<LeftOutlined />}
          onClick={handlePrev}
          style={{
            backgroundColor: 'transparent',
            color: colors.brand.accentStrong,
            border: `1px solid ${colors.brand.accent}`,
          }}
        />
      </div>
    );
  }
}
